import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Typography,
  LinearProgress,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  useTheme,
  alpha,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import Nodata from "../../assets/errors/nodata.png";

import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";

const LEAVE_TYPES = ["Annual", "Sick", "Compassionate", "Study", "Maternity"];

const MANAGER_ROLES = [
  "admin",
  "hr",
  "supervisor",
  "supperAdmin",
  "manager",
  "ceo",
];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// A staff member who is not active has left the organisation.
const isActiveStaff = (s) =>
  s?.status === "active" || s?.status === 1 || s?.status === "1";

/* ------------------------------------------------------------------ */
/* Main                                                                */
/* ------------------------------------------------------------------ */
const LeaveCredit = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { getData, addData, updateData, deleteData, userRole } = useAuth();

  const [leaves, setLeaves] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [selectedLeave, setSelectedLeave] = useState(null);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    user_id: "",
    leave_type: "Annual",
    leaveCredit: 20,
    usedUpCredit: 0,
    leftCredit: 20,
  });

  const canManage = MANAGER_ROLES.includes(userRole);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  const typeColor = useCallback(
    (type) =>
      ({
        Annual: colors.teal[500],
        Sick: colors.red[500],
        Compassionate: colors.indigo[500],
        Study: colors.newTeal[500],
        Maternity: colors.orange[500],
      }[type] || colors.gray[500]),
    [colors]
  );

  /* ---------------- Data ---------------- */
  const fetchData = useCallback(async () => {
    try {
      const [leavesResponse, usersResponse] = await Promise.all([
        getData("proccess.php", "leaveCredits_V2"),
        getData("proccess.php", "staffs"),
      ]);

      if (leavesResponse.success) setLeaves(leavesResponse.data || []);
      else toast.error(leavesResponse.message || "Failed to load leave credits");

      if (usersResponse.success) setStaffs(usersResponse.data || []);
      else toast.error(usersResponse.message || "Failed to load staff");
    } catch (err) {
      toast.error("Network error: Failed to fetch data");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Only current employees are ever rendered or selectable.
  const activeStaffs = useMemo(() => staffs.filter(isActiveStaff), [staffs]);

  /* ---------------- Group credits per staff member ---------------- */
  const cards = useMemo(() => {
    const byUser = leaves.reduce((acc, l) => {
      const key = String(l.user_id);
      (acc[key] = acc[key] || []).push(l);
      return acc;
    }, {});

    const rows = activeStaffs.map((staff) => {
      const records = byUser[String(staff.Id)] || [];
      const allocated = records.reduce((s, r) => s + num(r.leaveCredit), 0);
      const used = records.reduce((s, r) => s + num(r.usedUpCredit), 0);
      const left = records.reduce((s, r) => s + num(r.leftCredit), 0);
      return { staff, records, allocated, used, left };
    });

    // Credits whose user_id matches no staff record at all. Credits belonging to a
    // departed (inactive) staff member are intentionally dropped, not flagged.
    const orphanKeys = Object.keys(byUser).filter(
      (k) => !staffs.some((s) => String(s.Id) === k)
    );
    orphanKeys.forEach((k) => {
      const records = byUser[k];
      rows.push({
        staff: { Id: k, FirstName: "Unknown", LastName: "staff", orphan: true },
        records,
        allocated: records.reduce((s, r) => s + num(r.leaveCredit), 0),
        used: records.reduce((s, r) => s + num(r.usedUpCredit), 0),
        left: records.reduce((s, r) => s + num(r.leftCredit), 0),
      });
    });

    const q = search.trim().toLowerCase();
    const filtered = !q
      ? rows
      : rows.filter(({ staff, records }) =>
          [
            staff.FirstName,
            staff.LastName,
            staff.role,
            staff.department,
            ...records.map((r) => r.leave_type),
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        );

    // Staff with credits first, then alphabetical.
    return filtered.sort((a, b) => {
      if (!!a.records.length !== !!b.records.length) return b.records.length - a.records.length;
      return `${a.staff.FirstName}`.localeCompare(`${b.staff.FirstName}`);
    });
  }, [leaves, staffs, activeStaffs, search]);

  const totals = useMemo(
    () => ({
      staffWithCredits: cards.filter((c) => c.records.length).length,
      allocated: cards.reduce((s, c) => s + c.allocated, 0),
      used: cards.reduce((s, c) => s + c.used, 0),
      left: cards.reduce((s, c) => s + c.left, 0),
    }),
    [cards]
  );

  /* ---------------- Handlers ---------------- */
  const handleAddClick = (userId = "") => {
    setSelectedLeave(null);
    setFormData({
      user_id: userId,
      leave_type: "Annual",
      leaveCredit: 20,
      usedUpCredit: 0,
      leftCredit: 20,
    });
    setOpenFormDialog(true);
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    setFormData({
      user_id: leave.user_id || "",
      leave_type: leave.leave_type || "Annual",
      leaveCredit: num(leave.leaveCredit),
      usedUpCredit: num(leave.usedUpCredit),
      leftCredit: num(leave.leftCredit),
    });
    setOpenFormDialog(true);
  };

  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setOpenDeleteDialog(true);
  };

  const closeForm = () => {
    setOpenFormDialog(false);
    setSelectedLeave(null);
  };

  // leftCredit is always derived so the three numbers can never disagree.
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "leaveCredit" || name === "usedUpCredit") {
        next.leftCredit = Math.max(0, num(next.leaveCredit) - num(next.usedUpCredit));
      }
      return next;
    });
  };

  const duplicateType = useMemo(() => {
    if (!formData.user_id || !formData.leave_type) return false;
    return leaves.some(
      (l) =>
        String(l.user_id) === String(formData.user_id) &&
        l.leave_type === formData.leave_type &&
        (!selectedLeave || l.id !== selectedLeave.id)
    );
  }, [leaves, formData, selectedLeave]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.user_id) {
      toast.error("Please choose a staff member");
      return;
    }
    if (num(formData.usedUpCredit) > num(formData.leaveCredit)) {
      toast.error("Used credits cannot exceed allocated credits");
      return;
    }
    if (duplicateType) {
      toast.error(`This staff member already has a ${formData.leave_type} record`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        leaveCredit: num(formData.leaveCredit),
        usedUpCredit: num(formData.usedUpCredit),
        leftCredit: num(formData.leftCredit),
      };

      const response = selectedLeave
        ? await updateData("proccess.php", "leaveCredits_V2", payload, selectedLeave.id)
        : await addData("proccess.php", "leaveCredits_V2", payload);

      if (response.success) {
        toast.success(selectedLeave ? "Leave credits updated" : "Leave credits added");
        if (Array.isArray(response.data)) setLeaves(response.data);
        else await fetchData();
        closeForm();
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData(
        "proccess.php",
        "leaveCredits_V2",
        leaveToDelete.id
      );
      if (response.success) {
        toast.success("Leave credit deleted");
        setLeaves((prev) => prev.filter((l) => l.id !== leaveToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setLeaveToDelete(null);
  };

  /* ---------------- Small pieces ---------------- */
  const avatarSrc = (staff) =>
    staff.image
      ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}`
      : "";

  const initials = (staff) =>
    `${staff.FirstName?.[0] || ""}${staff.LastName?.[0] || ""}`.toUpperCase();

  const MiniStat = ({ label, value, color }) => (
    <Box sx={{ flex: 1, textAlign: "center" }}>
      <Typography
        variant="h3"
        sx={{ fontWeight: 800, lineHeight: 1.1, color: color || ui.textPrimary }}
      >
        {value}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          mt: 0.4,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: ui.textSecondary,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  /* ------------------------------------------------------------------ */
  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        {/* ------------------------------------------------------------ */}
        {/* Header                                                        */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{ fontWeight: 800, letterSpacing: -0.4, color: ui.textPrimary }}
            >
              Staff Leave Credits
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Allocate and adjust leave balances per staff member
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search staff, role or leave type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 19, color: ui.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleAddClick()}
              sx={primaryBtn}
            >
              Add leave credits
            </Button>
          </Stack>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* Summary strip                                                 */}
        {/* ------------------------------------------------------------ */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3.5,
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadow,
          }}
        >
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem sx={{ borderColor: ui.border }} />}
            spacing={2}
          >
            <MiniStat label="Staff covered" value={totals.staffWithCredits} />
            <MiniStat label="Allocated" value={totals.allocated} color={ui.accent} />
            <MiniStat label="Used" value={totals.used} color={colors.orange[500]} />
            <MiniStat label="Remaining" value={totals.left} color={colors.green[500]} />
          </Stack>
        </Paper>

        {/* ------------------------------------------------------------ */}
        {/* Staff cards                                                   */}
        {/* ------------------------------------------------------------ */}
        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`sk-${i}`}>
                <Skeleton
                  variant="rounded"
                  height={286}
                  sx={{ borderRadius: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
                />
              </Grid>
            ))}
          </Grid>
        ) : cards.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 10 }}>
            <Box component="img" src={Nodata} alt="No data" sx={{ width: 150, opacity: 0.85 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
              Nothing to show
            </Typography>
            <Typography variant="h6" sx={{ color: ui.textSecondary }}>
              {search
                ? "No active staff match your search."
                : "No active staff members found."}
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={2.5}>
            {cards.map(({ staff, records, allocated, used, left }) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={staff.Id}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    overflow: "hidden",
                    bgcolor: ui.surface,
                    border: `1px solid ${ui.border}`,
                    boxShadow: ui.shadow,
                    transition: "transform .2s ease, border-color .2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      borderColor: alpha(ui.accent, 0.4),
                    },
                  }}
                >
                  {/* Profile header */}
                  <Box
                    sx={{
                      p: 2.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.75,
                      background: `linear-gradient(180deg, ${alpha(
                        ui.accent,
                        isDark ? 0.1 : 0.07
                      )} 0%, transparent 100%)`,
                    }}
                  >
                    <Avatar
                      src={avatarSrc(staff)}
                      sx={{
                        width: 54,
                        height: 54,
                        fontWeight: 700,
                        fontSize: 18,
                        bgcolor: ui.surfaceAlt,
                        color: ui.textPrimary,
                        border: `2px solid ${alpha(ui.accent, 0.45)}`,
                      }}
                    >
                      {initials(staff)}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="h4"
                        noWrap
                        sx={{ fontWeight: 700, color: ui.textPrimary }}
                      >
                        {staff.FirstName} {staff.LastName}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.6 }}>
                        <Chip
                          size="small"
                          label={staff.orphan ? "orphaned record" : staff.role || "staff"}
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            textTransform: "capitalize",
                            color: staff.orphan ? colors.red[500] : ui.accent,
                            bgcolor: alpha(staff.orphan ? colors.red[500] : ui.accent, 0.12),
                          }}
                        />
                        <Typography variant="h6" noWrap sx={{ color: ui.textSecondary }}>
                          {staff.department || "No department"}
                        </Typography>
                      </Stack>
                    </Box>

                    {canManage && !staff.orphan && (
                      <Tooltip title="Add a leave type for this staff member">
                        <IconButton
                          onClick={() => handleAddClick(staff.Id)}
                          sx={{
                            color: ui.accent,
                            border: `1px solid ${alpha(ui.accent, 0.35)}`,
                            borderRadius: 2,
                            "&:hover": { bgcolor: alpha(ui.accent, 0.12) },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Totals */}
                  <Stack
                    direction="row"
                    divider={
                      <Divider orientation="vertical" flexItem sx={{ borderColor: ui.border }} />
                    }
                    sx={{
                      px: 2,
                      py: 1.75,
                      bgcolor: alpha(ui.surfaceAlt, isDark ? 0.55 : 0.7),
                      borderTop: `1px solid ${ui.border}`,
                      borderBottom: `1px solid ${ui.border}`,
                    }}
                  >
                    <MiniStat label="Allocated" value={allocated} />
                    <MiniStat label="Used" value={used} color={colors.orange[500]} />
                    <MiniStat label="Left" value={left} color={colors.green[500]} />
                  </Stack>

                  {/* Leave type breakdown */}
                  <Box sx={{ p: 2.5, flex: 1 }}>
                    {records.length === 0 ? (
                      <Stack alignItems="center" spacing={1.25} sx={{ py: 3 }}>
                        <EventAvailableOutlinedIcon
                          sx={{ fontSize: 32, color: ui.textSecondary, opacity: 0.6 }}
                        />
                        <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                          No leave credits assigned yet
                        </Typography>
                        {canManage && (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddClick(staff.Id)}
                            sx={{
                              mt: 0.5,
                              textTransform: "none",
                              fontWeight: 700,
                              color: ui.accent,
                              "&:hover": { bgcolor: alpha(ui.accent, 0.1) },
                            }}
                          >
                            Assign credits
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      records.map((rec) => {
                        const total = num(rec.leaveCredit);
                        const spent = num(rec.usedUpCredit);
                        const remaining = num(rec.leftCredit);
                        const color = typeColor(rec.leave_type);
                        const mismatch = total - spent !== remaining;

                        return (
                          <Box key={rec.id} sx={{ mb: 2 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mb: 0.75 }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.85}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: color,
                                  }}
                                />
                                <Typography
                                  variant="h5"
                                  sx={{ fontWeight: 700, color: ui.textPrimary }}
                                >
                                  {rec.leave_type}
                                </Typography>
                                {mismatch && (
                                  <Tooltip
                                    title={`Numbers disagree: ${total} allocated − ${spent} used should leave ${
                                      total - spent
                                    }, but the record says ${remaining}.`}
                                  >
                                    <WarningAmberRoundedIcon
                                      sx={{ fontSize: 16, color: colors.orange[500] }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>

                              <Stack direction="row" alignItems="center" spacing={0.25}>
                                <Typography
                                  variant="h6"
                                  sx={{ mr: 0.75, color: ui.textSecondary }}
                                >
                                  <Box component="span" sx={{ fontWeight: 800, color: ui.textPrimary }}>
                                    {remaining}
                                  </Box>{" "}
                                  of {total} left
                                </Typography>

                                {canManage && (
                                  <>
                                    <Tooltip title="Edit credits">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditClick(rec)}
                                        sx={{
                                          color: ui.textSecondary,
                                          "&:hover": {
                                            color: ui.accent,
                                            bgcolor: alpha(ui.accent, 0.12),
                                          },
                                        }}
                                      >
                                        <EditNoteOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete this leave type">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteClick(rec)}
                                        sx={{
                                          color: ui.textSecondary,
                                          "&:hover": {
                                            color: colors.red[500],
                                            bgcolor: alpha(colors.red[500], 0.12),
                                          },
                                        }}
                                      >
                                        <DeleteForeverOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Stack>
                            </Stack>

                            <Tooltip title={`${spent} used · ${remaining} remaining`}>
                              <LinearProgress
                                variant="determinate"
                                value={total ? Math.min(100, (spent / total) * 100) : 0}
                                sx={{
                                  height: 7,
                                  borderRadius: 999,
                                  bgcolor: alpha(color, 0.18),
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    backgroundColor: color,
                                  },
                                }}
                              />
                            </Tooltip>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Add / Edit dialog                                               */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openFormDialog}
        onClose={closeForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            {selectedLeave ? "Edit leave credits" : "Add leave credits"}
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
            Remaining credits are calculated automatically.
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1.5 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  id="user_id"
                  name="user_id"
                  label="Staff member"
                  value={formData.user_id}
                  onChange={handleFormChange}
                  required
                  disabled={!!selectedLeave}
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>Select staff…</em>
                  </MenuItem>
                  {activeStaffs.map((staff) => (
                    <MenuItem key={staff.Id} value={staff.Id}>
                      {staff.FirstName} {staff.LastName}
                      {staff.department ? ` — ${staff.department}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  id="leave_type"
                  name="leave_type"
                  label="Leave type"
                  value={formData.leave_type}
                  onChange={handleFormChange}
                  required
                  error={duplicateType}
                  helperText={
                    duplicateType ? "This staff member already has this leave type." : " "
                  }
                  sx={fieldSx}
                >
                  {LEAVE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: typeColor(t),
                          }}
                        />
                        <span>{t}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  id="leaveCredit"
                  name="leaveCredit"
                  label="Allocated"
                  type="number"
                  value={formData.leaveCredit}
                  onChange={handleFormChange}
                  required
                  inputProps={{ min: 0 }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  id="usedUpCredit"
                  name="usedUpCredit"
                  label="Used"
                  type="number"
                  value={formData.usedUpCredit}
                  onChange={handleFormChange}
                  required
                  error={num(formData.usedUpCredit) > num(formData.leaveCredit)}
                  inputProps={{ min: 0, max: num(formData.leaveCredit) }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  id="leftCredit"
                  name="leftCredit"
                  label="Remaining"
                  type="number"
                  value={formData.leftCredit}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-calculated"
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeForm}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || duplicateType}
            endIcon={<SendIcon />}
            sx={primaryBtn}
          >
            {saving ? "Saving…" : selectedLeave ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Delete dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            Delete leave credit
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5" sx={{ color: ui.textSecondary, lineHeight: 1.6 }}>
            Remove the{" "}
            <Box component="span" sx={{ fontWeight: 800, color: ui.textPrimary }}>
              {leaveToDelete?.leave_type}
            </Box>{" "}
            allocation for this staff member? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
            sx={{
              px: 2.75,
              py: 1.1,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.red[500],
              "&:hover": { bgcolor: colors.red[600] },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveCredit;